/**
 * D-EZ-110 — WHAT THE DRIVERS SAY BETWEEN SEASONS.
 *
 * The people layer's voice bank (`people/voiceBank.ts`) covers race days. The
 * winter and the preseason have their own facts — a man changes shops, walks
 * away, loses his ride, arrives as a rookie, is asked whether this is his last
 * year, goes into the Hall of Fame — and this bank says them in the same six
 * voices, under the same rules (`chapters.test.ts` holds every line to them):
 *
 *   • NO DIGITS, NO NAMES, NO RESULTS of their own. A name or a number comes in
 *     only through a slot — `{team}` (his shop now), `{old}` (the shop he left),
 *     `{sponsor}`, `{other}` (another driver, by surname) — and a line whose slot
 *     the fact cannot fill is never chosen.
 *   • NOTHING THE FACT DOES NOT SAY. "last-ride" lines never say yes or no —
 *     the winter has not been run; a man asked whether he is retiring answers
 *     like a man who does not know either.
 *   • NO WORDS IN A REAL MAN'S MOUTH: `winterQuote` returns null for a 2025
 *     roster driver (`rw25-…`), exactly as `people/quotes.ts` does.
 *
 * Choice of line is a hash of (league, driver, fact, season) — never a die.
 */

import type { Voice } from '../people/personality.ts';

export type WinterFact =
  | 'moved'
  | 'moved-up'
  | 'stayed'
  | 'retiring'
  | 'let-go'
  | 'rookie'
  | 'new-sponsor'
  | 'defending'
  | 'last-ride'
  | 'rival-preseason'
  | 'comeback'
  | 'inducted'
  | 'tribute';

type Bank = Record<WinterFact, readonly string[]>;

const HOTHEAD: Bank = {
  moved: [
    'I wanted to win and I didn’t think I could do it at {old}. Simple as that.',
    '{team} wants to win as bad as I do. That’s all I needed to hear.',
    'Some folks at {old} are going to be mad. They’ll get over it.',
    'New shop, same attitude. Somebody’s going to find out real quick.',
  ],
  'moved-up': [
    'I’ve been banging on doors for a long time. {team} finally opened one.',
    'No more scraping for tires. Now we find out what I can really do.',
    'Running my own deal taught me plenty. Now watch me with real equipment.',
  ],
  stayed: [
    'Why would I leave? We’re going to win this thing at {team}.',
    'People kept asking if I was going somewhere. I’m not. Quit asking.',
    'This is my shop. They’ll have to run me off.',
  ],
  retiring: [
    'I gave it everything I had. Every lap. I don’t regret a one of ’em.',
    'I’m done, but don’t think I’ve gone soft. I just know when it’s time.',
    'I’ll miss the fight. Not the losing — the fight.',
    'Some of y’all are going to miss having me to write about.',
  ],
  'let-go': [
    'This ain’t the end of me. Write that down.',
    'They can take the ride. They can’t take what I know.',
    'I’ll be back, and I’ll remember who didn’t want me.',
  ],
  rookie: [
    'I didn’t come here to ride around. I came here to beat people.',
    'Everybody keeps calling me a rookie. Fine. They won’t for long.',
    'I don’t care whose name is on the trophy case. I’ll race anybody.',
  ],
  'new-sponsor': [
    '{sponsor} is on the car now, and I aim to put it in victory lane.',
    'New paint, new money, and I’m in a hurry. Thank you, {sponsor}.',
    'Folks at {sponsor} picked a winner. I intend to prove it.',
  ],
  defending: [
    'Everybody’s coming for us. Good. Let ’em come.',
    'They’ll have to take it from me. I sure ain’t handing it over.',
    'I don’t defend anything. I go win it again.',
  ],
  'last-ride': [
    'Retire? I’ll tell you when I’m good and ready. Not before.',
    'Ask me after the season. Right now I want to beat these kids.',
    'Everybody wants to put me out to pasture. Not yet, they don’t.',
  ],
  'rival-preseason': [
    'Me and {other} have unfinished business. Everybody knows it.',
    'I haven’t forgotten anything about {other}. Not one thing.',
    'If {other} wants it again this year, I’m right here.',
  ],
  comeback: [
    'I’ve sat on that couch long enough to go crazy. Let’s go racing.',
    'Doctors cleared me. Now I’m clearing out anybody in my way.',
    'Nobody missed racing more than me. Nobody.',
  ],
  inducted: [
    'I rubbed a lot of people the wrong way to get here. Worth it.',
    'I never raced for a trophy like this. I raced to beat people. Guess it worked.',
    'Half the guys in this room wanted to fight me. The other half did.',
  ],
  tribute: [
    'Me and {other} went at it for years. I wouldn’t trade a minute of it.',
    'I couldn’t stand racing {other}. That’s about the best compliment I’ve got.',
    '{other} made me better, and I hated every second of it.',
  ],
};

const ICE: Bank = {
  moved: [
    'It was the right time to make a change. Nothing more to it than that.',
    'I’m grateful to everybody at {old}. Now the work starts at {team}.',
    '{team} had a plan I believed in. That’s what decided it.',
  ],
  'moved-up': [
    'A charter seat is a chance. I intend to use it well.',
    'We did a lot with a little. I’m curious what we can do with more.',
    'Grateful for the call from {team}. Now it’s about execution.',
  ],
  stayed: [
    'We have something good at {team}. No reason to change it.',
    'Continuity matters. Same people, same plan.',
    'I never really thought about leaving.',
  ],
  retiring: [
    'It felt like the right time. I’m at peace with it.',
    'I got more out of this sport than I ever expected to.',
    'No big speech. Thank you to everybody who helped along the way.',
  ],
  'let-go': [
    'It’s a results business. I understand that.',
    'I’ll take some time and look at what comes next.',
    'Disappointing. I’ll work on it and see where it leads.',
  ],
  rookie: [
    'I’m here to learn and keep the car in one piece.',
    'Finish races, stay out of trouble, and the rest takes care of itself.',
    'There’s a lot to absorb. I’ll take it a week at a time.',
  ],
  'new-sponsor': [
    'Good to have {sponsor} with us. We’ll do our part.',
    'Glad to have {sponsor} on board. It helps the whole program.',
    'A new partner is a good start. Results are the next step.',
  ],
  defending: [
    'Last year is last year. It doesn’t give us any points.',
    'We start at zero like everybody else.',
    'Same approach. It worked once; no reason it can’t work again.',
  ],
  'last-ride': [
    'I haven’t decided anything. I’ll know when I know.',
    'That’s a conversation for the winter.',
    'I’m focused on this season. The rest can wait.',
  ],
  'rival-preseason': [
    '{other} and I have raced hard. I expect that to continue.',
    'I don’t carry much over the winter. I’ll race {other} clean.',
    'Whatever happened with {other} last year stays last year.',
  ],
  comeback: [
    'I feel good. Healthy, and ready to work.',
    'The time off gave me a chance to think. I’m ready.',
    'Cleared, rested, and glad to be back in the car.',
  ],
  inducted: [
    'I’m not one for speeches. This means a great deal to me.',
    'A lot of good people put me in a position to do this. They share it.',
    'I tried to do it the right way. It’s nice to be recognised for that.',
  ],
  tribute: [
    '{other} was as good as anybody I raced against. Deserves it.',
    'Racing {other} meant you had to be at your best. Every week.',
    'Not many raced as clean or as hard as {other}.',
  ],
};

const CORPORATE: Bank = {
  moved: [
    'I want to thank everyone at {old}, and I’m excited for this opportunity with {team}.',
    '{team} is a first-class organization and I’m proud to be part of it.',
    'Great people at {old}. Great people at {team}. I’m a lucky guy.',
  ],
  'moved-up': [
    'Huge thanks to {team} for believing in me. We’ll make them proud.',
    'This is a big step for our whole program, and I’m grateful.',
    'A charter seat with {team} — I couldn’t ask for a better opportunity.',
  ],
  stayed: [
    'I’m proud to represent {team} again. It’s a family here.',
    'Everyone at {team} has my back, and I have theirs.',
    'Glad to keep this partnership going. We have big goals.',
  ],
  retiring: [
    'I want to thank every sponsor, every crew member and every fan. What a ride.',
    'This sport gave me everything. I’m grateful to every partner along the way.',
    'Thank you to the fans. You made every weekend special.',
  ],
  'let-go': [
    'I’m thankful for the opportunity and I wish the team well.',
    'I appreciate everyone who supported us. We’ll regroup.',
    'It’s been an honor. I’m looking forward to what’s next.',
  ],
  rookie: [
    'I’m just thankful for the opportunity, and I want to represent my partners well.',
    'It’s a dream come true. I want to thank everybody who got me here.',
    'Proud to be here, and I’ll work hard for everyone who believed in me.',
  ],
  'new-sponsor': [
    'We’re thrilled to welcome {sponsor}. Great people, great brand.',
    'Proud to carry the {sponsor} colors this season.',
    '{sponsor} shares our values, and we’re excited to get to work.',
  ],
  defending: [
    'Our partners did a great job last year, and we want to deliver again.',
    'We’re proud of last season and hungry for more.',
    'Defending it is an honor. We’ll give it everything.',
  ],
  'last-ride': [
    'Right now I’m focused on delivering for our partners this season.',
    'We’ll sit down as a team at the right time. Today’s about racing.',
    'I love what I do, and I’m grateful for every season I get.',
  ],
  'rival-preseason': [
    'I have a lot of respect for {other}. We’ll race hard and race clean.',
    '{other} is a great competitor. That’s good for the fans.',
    'Racing {other} brings out the best in our whole team.',
  ],
  comeback: [
    'Thank you to the doctors, the team and the fans for all the support.',
    'Grateful to be cleared and grateful to my partners for standing by me.',
    'It means a lot to have everybody’s support. I’m ready to go.',
  ],
  inducted: [
    'This honor belongs to every crew member and partner who ever stood behind me.',
    'Thank you to the fans, the teams and the sponsors. I’m humbled.',
    'I’m grateful to this sport and to everyone who made it possible.',
  ],
  tribute: [
    'Nobody deserves this more than {other}. A great champion and a great ambassador.',
    '{other} set the standard, and I’m proud to have raced alongside.',
    'Congratulations to {other}. Well earned.',
  ],
};

const SURLY: Bank = {
  moved: [
    'It’s a ride. They pay, I drive.',
    'Didn’t work out at {old}. Moving on.',
    'Don’t make a big deal out of it.',
  ],
  'moved-up': [
    'About time.',
    'Got a seat. Now leave me alone and let me drive it.',
    'Somebody finally noticed. Took ’em long enough.',
  ],
  stayed: [
    'Same shop. Same answer. Next question.',
    'Not going anywhere. That’s it.',
    'Why does everybody keep asking me that?',
  ],
  retiring: [
    'I’m done. That’s the story.',
    'Don’t need a party. I just need a nap.',
    'It’s over. Y’all can quit asking now.',
  ],
  'let-go': [
    'No comment.',
    'Ask them. Not me.',
    'I’ve got nothing to say about it.',
  ],
  rookie: [
    'I’m here to drive, not talk.',
    'Ask me after I’ve run a race.',
    'Don’t know what you want me to say.',
  ],
  'new-sponsor': [
    'Different paint. Same car.',
    '{sponsor}’s on the hood. Good for them.',
    'Sponsor’s fine. Ask them about it.',
  ],
  defending: [
    'Won it once. Gonna try and win it again. What else is there?',
    'Same as last year. Go fast.',
    'Nobody hands it to you twice.',
  ],
  'last-ride': [
    'Ask me in the winter.',
    'I don’t know. Next question.',
    'Not today.',
  ],
  'rival-preseason': [
    'I’m not talking about {other}.',
    '{other}? Next question.',
    'Ask {other}.',
  ],
  comeback: [
    'I’m fine. Let’s go.',
    'Healed up. Done talking about it.',
    'Back. That’s all.',
  ],
  inducted: [
    'Didn’t expect this. Appreciate it, I guess.',
    'Never was much for talking. Thanks.',
    'Guess I did all right.',
  ],
  tribute: [
    '{other} could drive. Not much more to say.',
    'Hated racing {other}. Respect, though.',
    'Good for {other}.',
  ],
};

const QUOTABLE: Bank = {
  moved: [
    'You know what they say — when the phone rings and it’s {team}, you pick it up.',
    'I’ll always have a soft spot for {old}. But a man has to chase it while he can.',
    'New shop, new hauler, new coffee pot. Same lead foot.',
    'Leaving {old} was the hardest phone call I ever made. Answering {team} was the easiest.',
  ],
  'moved-up': [
    'I’ve been hauling my own car on an open trailer. Now I get a hauler with a couch in it.',
    'From begging for tires to a charter seat with {team}. I might frame this.',
    'They finally let the stray dog in the house.',
  ],
  stayed: [
    'People keep trying to sell me on greener grass. The grass at {team} is just fine.',
    'I’ve been at {team} so long they named a parking spot after me. Well — almost.',
    'Why move? I just figured out where they keep the good snacks.',
  ],
  retiring: [
    'I’ve promised to be home on Sundays. Nobody at the house believes me.',
    'I’ll miss the racing. I won’t miss the airports.',
    'This old body has been trying to retire for years. I finally let it.',
    'The car always knew when I was tired. Now I do too.',
  ],
  'let-go': [
    'Every great comeback starts with a bad phone call.',
    'I’ve been knocked down before. Ask anybody who’s raced me.',
    'They say this is a people business. Turns out it’s a results business.',
  ],
  rookie: [
    'I used to watch these guys on TV. Now I have to pass them. That’s strange.',
    'Back home they still ask me if this is a real job. I’ll let ’em know.',
    'I have a lot to learn and a whole season of weekends to learn it in.',
  ],
  'new-sponsor': [
    '{sponsor} is on the car, so I guess I’d better win something.',
    'Shiny new {sponsor} paint. I promise to try and keep it shiny.',
    'Somebody at {sponsor} took a chance on me. I like those people.',
  ],
  defending: [
    'Everybody wants a piece of the champ. I’ll try and leave ’em some crumbs.',
    'The trophy is a lot heavier when you’re trying to keep it.',
    'Last year was a great story. Now I want a sequel.',
  ],
  'last-ride': [
    'Every year somebody asks. Every year I tell ’em ask me in the winter.',
    'I’ve got one foot in the car and one foot on the couch. We’ll see which one wins.',
    'The day I stop wanting to beat these kids, you’ll be the first to know.',
  ],
  'rival-preseason': [
    'Me and {other}? Best show in the sport. Buy a ticket.',
    '{other} and I sent each other a holiday card. I think it was ticking.',
    'If you’re looking for fireworks, sit near me and {other}.',
  ],
  comeback: [
    'I spent the time off watching races and yelling at the TV. Now they can hear me.',
    'The doctors said rest. I rested. Never again.',
    'I’ve watched enough racing from the couch to last a lifetime.',
  ],
  inducted: [
    'If you’d told that kid sweeping the shop floor he’d be standing here, he’d have laughed at you.',
    'I’ve been in a lot of halls. This is the first one that let me stay.',
    'I just drove the car. Everybody else made it go fast.',
  ],
  tribute: [
    'I spent half my career looking at {other}’s back bumper. Now I get to look at a plaque.',
    'If {other} wasn’t out there, I’d have won a whole lot more. I’d have enjoyed it a whole lot less.',
    'Racing {other} was never boring. Painful, sometimes. Never boring.',
  ],
};

const PLAIN: Bank = {
  moved: [
    'It’s a new start with {team}. I’m looking forward to it.',
    'I learned a lot at {old}. Time for the next step.',
    'Good people at {team}. I think we can win together.',
  ],
  'moved-up': [
    'Getting a charter seat with {team} means a lot.',
    'It’s a big step. I’m ready for it.',
    'Running my own car was hard. This is a chance to show what I can do.',
  ],
  stayed: [
    'I like it at {team}. No reason to leave.',
    'Same team, same goals. We want more this year.',
    'We’re building something. I want to be here for it.',
  ],
  retiring: [
    'It’s time. I’ve had a good run.',
    'I’ll miss it, but I’m ready.',
    'Thanks to everybody who helped me along the way.',
  ],
  'let-go': [
    'It’s tough. I’ll figure out what’s next.',
    'Not the way I wanted it to go.',
    'I’ll keep working and see what happens.',
  ],
  rookie: [
    'I just want to learn and run good.',
    'It’s a big step up. I’m excited.',
    'I’ll take it one race at a time.',
  ],
  'new-sponsor': [
    'Glad to have {sponsor} with us this year.',
    'Good to have {sponsor} on the car.',
    'New sponsor, same job. Run up front.',
  ],
  defending: [
    'We want to do it again. That’s the goal.',
    'Every week matters the same as it did last year.',
    'We know what it takes now. We’ll try to do it again.',
  ],
  'last-ride': [
    'I haven’t made up my mind. We’ll talk about it later.',
    'Right now I just want to race.',
    'I’ll think about all that when the season’s over.',
  ],
  'rival-preseason': [
    'I’ll race {other} like I race anybody. Hard.',
    'We’ve had our moments, me and {other}. It’s racing.',
    'I don’t think about {other} much. I think about winning.',
  ],
  comeback: [
    'I feel good. Ready to go.',
    'Glad to be back.',
    'It was a long time out. I’m ready.',
  ],
  inducted: [
    'This means a lot. Thank you.',
    'I never expected anything like this.',
    'I owe a lot of people for this one.',
  ],
  tribute: [
    '{other} was one of the best. Glad to see it.',
    'Raced {other} a lot of years. Deserves it.',
    'Good driver, good man. Congratulations to {other}.',
  ],
};

export const WINTER_BANK: Readonly<Record<Voice, Bank>> = {
  hothead: HOTHEAD,
  ice: ICE,
  corporate: CORPORATE,
  surly: SURLY,
  quotable: QUOTABLE,
  plain: PLAIN,
};

export const WINTER_FACTS = Object.keys(PLAIN) as WinterFact[];
export const WINTER_SLOTS = ['team', 'old', 'sponsor', 'other'] as const;
